import { MapNode, NodeType } from '../types';
import { SeededRandom } from '../utils/Random';

// Act 1 layout: 7 rows
const ACT1_ROW_CONFIG: { count: number; types: NodeType[] }[] = [
  { count: 1, types: ['combat'] },
  { count: 3, types: ['combat', 'combat', 'event'] },
  { count: 3, types: ['combat', 'event', 'combat'] },
  { count: 2, types: ['rest', 'event'] },
  { count: 3, types: ['elite', 'combat', 'combat'] },
  { count: 2, types: ['combat', 'elite'] },
  { count: 1, types: ['boss'] },
];

// Act 2: harder, more elites, more events
const ACT2_ROW_CONFIG: { count: number; types: NodeType[] }[] = [
  { count: 2, types: ['combat', 'event'] },
  { count: 3, types: ['combat', 'elite', 'event'] },
  { count: 3, types: ['elite', 'combat', 'combat'] },
  { count: 2, types: ['rest', 'event'] },
  { count: 3, types: ['elite', 'elite', 'combat'] },
  { count: 3, types: ['combat', 'event', 'elite'] },
  { count: 2, types: ['rest', 'combat'] },
  { count: 1, types: ['boss'] },
];

export function generateMap(seed: number, act: number): MapNode[][] {
  const rng = new SeededRandom(seed + act * 1000);
  const config = act >= 2 ? ACT2_ROW_CONFIG : ACT1_ROW_CONFIG;

  const map: MapNode[][] = [];

  // Generate nodes per row
  for (let row = 0; row < config.length; row++) {
    const rowConfig = config[row];
    const types = rng.shuffle([...rowConfig.types]).slice(0, rowConfig.count);
    const rowNodes: MapNode[] = [];

    for (let col = 0; col < types.length; col++) {
      rowNodes.push({
        id: `${row}-${col}`,
        row,
        col,
        type: types[col],
        connections: [],
      });
    }
    map.push(rowNodes);
  }

  // Generate connections (each node connects to 1-2 nodes in next row)
  for (let row = 0; row < map.length - 1; row++) {
    const currentRow = map[row];
    const nextRow = map[row + 1];
    if (nextRow.length === 0) continue;

    // Ensure every node in current row has at least 1 connection
    for (const node of currentRow) {
      const targetIdx = Math.min(node.col, nextRow.length - 1);
      const target = nextRow[targetIdx];
      if (!node.connections.includes(target.id)) {
        node.connections.push(target.id);
      }

      // 60% chance of a second connection to adjacent node
      if (rng.int(0, 100) < 60 && nextRow.length > 1) {
        const altIdx = targetIdx === 0 ? 1 : targetIdx - 1;
        const alt = nextRow[altIdx];
        if (!node.connections.includes(alt.id)) {
          node.connections.push(alt.id);
        }
      }
    }

    // Ensure every node in next row is reachable by at least 1 node
    for (const target of nextRow) {
      const hasIncoming = currentRow.some(n => n.connections.includes(target.id));
      if (!hasIncoming) {
        // Connect from nearest node in current row
        const nearest = currentRow.reduce((best, n) =>
          Math.abs(n.col - target.col) < Math.abs(best.col - target.col) ? n : best
        );
        nearest.connections.push(target.id);
      }
    }
  }

  return map;
}
